import React, {
  RefObject,
} from 'react';
import isNil from 'lodash/isNil';
import {
  notEmptyString,
} from '@populateui/utils';
import {
  ExternalLink,
  PrimaryButton,
  UnavailableLink,
} from 'views/EMR/components/PCC/Walkthrough/components/ContentPrimitives';
import PageIntroduction from 'views/EMR/components/PCC/Walkthrough/components/PageIntroduction';
import {
  walkthroughContent,
} from 'views/EMR/components/PCC/Walkthrough/content';
import WalkthroughIcon from 'views/EMR/components/PCC/Walkthrough/icons';
import type {
  AgreementPageDefinition,
} from 'views/EMR/components/PCC/Walkthrough/types';

type Props = {
  page: AgreementPageDefinition;
  headingRef: RefObject<HTMLHeadingElement | null>;
  isAgreed: boolean;
  onAgreedChange: (checked: boolean) => void;
  onActivate: () => void;
  guideUrl?: string;
  onOpenGuide?: () => void;
  disabled?: boolean;
};

export default function AgreementPage({
  page,
  headingRef,
  isAgreed,
  onAgreedChange,
  onActivate,
  guideUrl,
  onOpenGuide,
  disabled = false,
}: Props) {
  const canActivate = isAgreed && !disabled;
  const hasGuideUrl = notEmptyString(guideUrl);
  const hasGuideCallback = !isNil(onOpenGuide);
  const checkboxId = 'pcc-walkthrough-agreement-checkbox';

  return (
    <div className="flex flex-col gap-7">
      <PageIntroduction
        eyebrow={page.introduction.eyebrow}
        heading={page.introduction.heading}
        description={page.introduction.description}
        headingRef={headingRef}
      />

      <div className="flex flex-col gap-[18px] rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-7">
        <p className="m-0 text-[15px] leading-[1.55] text-[#475569]">
          {page.agreement.introduction}
        </p>

        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {page.agreement.items.map((item) => (
            <li
              key={item}
              className="flex items-start gap-3 text-[15px] leading-normal text-[#0F1B2D]"
            >
              <WalkthroughIcon
                iconKey="check"
                className="mt-0.5 !h-[18px] !w-[18px] shrink-0 !text-brand-500"
              />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        {hasGuideUrl ? (
          <ExternalLink
            href={guideUrl!}
            label={page.agreement.guideLinkLabel}
          />
        ) : null}
        {!hasGuideUrl && hasGuideCallback ? (
          <button
            type="button"
            disabled={disabled}
            onClick={onOpenGuide}
            className="self-start text-[15px] font-semibold text-brand-700 hover:text-brand-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
          >
            {page.agreement.guideLinkLabel}
          </button>
        ) : null}
        {!hasGuideUrl && !hasGuideCallback ? (
          <UnavailableLink
            label={page.agreement.guideLinkLabel}
            helperText={walkthroughContent.shared.guideUnavailableLabel}
          />
        ) : null}

        <div className="h-px bg-[#E2E8F0]" />

        <div className="flex items-start gap-3">
          <input
            id={checkboxId}
            type="checkbox"
            checked={isAgreed}
            disabled={disabled}
            onChange={(event) => onAgreedChange(event.target.checked)}
            className="mt-0.5 h-[22px] w-[22px] shrink-0 accent-brand-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
          />
          <label
            htmlFor={checkboxId}
            className="text-[15px] leading-normal text-[#0F1B2D]"
          >
            {page.agreement.checkboxLabel}
          </label>
        </div>

        <div className="flex flex-col gap-2">
          <PrimaryButton
            className="!w-full max-w-[260px] !justify-center"
            disabled={!canActivate}
            onClick={onActivate}
          >
            {page.agreement.activateLabel}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}
